// src/pages/onboarding/OnboardingLeadAdsPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import OnboardingLayout from "./OnboardingLayout";

import {
  Bot,
  BotChannel,
  getBotById,
  fetchChannels,
  fetchWhatsappTemplates,
  createWhatsappTemplate,
  updateWhatsappTemplate,
  WhatsappTemplate,
  WhatsappTemplateStatus,
  WhatsappTemplateUpsertPayload,
  WhatsappTemplateComponent,
  fetchMetaLeadAutomation,
  upsertMetaLeadAutomation,
  MetaLeadAutomationSettings,
  updateBot
} from "../../api/bots";

/**
 * Template form helpers (copied & trimmed from BotWhatsappTemplatesPage)
 */

interface TemplateFormButton {
  id: string;
  type: "QUICK_REPLY" | "URL" | "PHONE_NUMBER";
  text: string;
  url?: string;
  phone_number?: string;
}

interface TemplateFormState {
  name: string;
  language: string;
  category: "UTILITY" | "MARKETING" | "AUTHENTICATION";
  headerEnabled: boolean;
  headerText: string;
  bodyText: string;
  footerEnabled: boolean;
  footerText: string;
  buttons: TemplateFormButton[];
}

const DEFAULT_FORM_STATE: TemplateFormState = {
  name: "",
  language: "en_US",
  category: "UTILITY",
  headerEnabled: false,
  headerText: "",
  bodyText: "",
  footerEnabled: false,
  footerText: "",
  buttons: []
};

function toStatusClass(status: WhatsappTemplateStatus) {
  switch (status) {
    case "APPROVED":
      return "status-badge status-badge-ok";
    case "PENDING":
      return "status-badge status-badge-warn";
    case "REJECTED":
      return "status-badge status-badge-error";
    default:
      return "status-badge";
  }
}

function buildComponentsFromForm(
  form: TemplateFormState
): WhatsappTemplateComponent[] {
  const components: WhatsappTemplateComponent[] = [];

  if (form.headerEnabled && form.headerText.trim()) {
    components.push({
      type: "HEADER",
      format: "TEXT",
      text: form.headerText.trim(),
      example: form.headerText.includes("{{")
        ? {
            header_text: ["Example header"]
          }
        : undefined
    });
  }

  if (form.bodyText.trim()) {
    const bodyText = form.bodyText.trim();
    const matches = bodyText.match(/\{\{\d+\}\}/g) || [];
    components.push({
      type: "BODY",
      text: bodyText,
      example:
        matches.length > 0
          ? {
              body_text: matches.map((_, idx) => `Example ${idx + 1}`)
            }
          : undefined
    });
  }

  if (form.footerEnabled && form.footerText.trim()) {
    components.push({
      type: "FOOTER",
      text: form.footerText.trim()
    });
  }

  const buttons = form.buttons
    .filter((b) => b.text.trim())
    .map((b) => ({
      type: b.type,
      text: b.text.trim(),
      url: b.type === "URL" ? b.url : undefined,
      phone_number: b.type === "PHONE_NUMBER" ? b.phone_number : undefined
    }));

  if (buttons.length > 0) {
    components.push({
      type: "BUTTONS",
      buttons
    });
  }

  return components;
}

function formStateFromTemplate(tpl: WhatsappTemplate): TemplateFormState {
  const header = tpl.components.find((c) => c.type === "HEADER");
  const body = tpl.components.find((c) => c.type === "BODY");
  const footer = tpl.components.find((c) => c.type === "FOOTER");
  const buttonsComp = tpl.components.find((c) => c.type === "BUTTONS");

  return {
    name: tpl.name,
    language: tpl.language as any,
    category: tpl.category as any,
    headerEnabled: !!header,
    headerText: header?.text ?? "",
    bodyText: body?.text ?? "",
    footerEnabled: !!footer,
    footerText: footer?.text ?? "",
    buttons:
      buttonsComp?.buttons?.map((b, idx) => ({
        id: `${tpl.id}-btn-${idx}`,
        type: b.type,
        text: b.text,
        url: b.url,
        phone_number: b.phone_number
      })) ?? []
  };
}

const OnboardingLeadAdsPage: React.FC = () => {
  const { id: botId } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [bot, setBot] = useState<Bot | null>(null);
  const [channels, setChannels] = useState<BotChannel[]>([]);
  const [templates, setTemplates] = useState<WhatsappTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  const [error, setError] = useState<string | null>(null);

  // Lead Ads → WhatsApp automation config draft
  const [leadConfigDraft, setLeadConfigDraft] =
    useState<MetaLeadAutomationSettings>({
      phoneFieldName: "phone_number",
      consentFieldName: "whatsapp_opt_in",
      requiresWhatsappOptIn: true,
      templateName: "",
      templateLanguage: ""
    });
  const [leadConfigLoading, setLeadConfigLoading] = useState(false);

  // Whether the Lead Ads feature is ON in onboarding
  const [enabled, setEnabled] = useState<boolean>(false);
  const [saving, setSaving] = useState(false);

  // Template selection & editing
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null
  );
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [formTemplateId, setFormTemplateId] = useState<string | null>(null);
  const [formState, setFormState] =
    useState<TemplateFormState>(DEFAULT_FORM_STATE);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);

  // Load bot + channels
  useEffect(() => {
    if (!botId) return;

    async function load() {
      try {
        setLoading(true);
        setError(null);
        const [botRes, channelRes] = await Promise.all([
          getBotById(botId!),
          fetchChannels(botId!)
        ]);
        setBot(botRes);
        setChannels(channelRes);
      } catch (err: any) {
        console.error(err);
        setError(err?.message || "Failed to load bot or channels.");
      } finally {
        setLoading(false);
      }
    }

    void load();
  }, [botId]);

  const whatsappChannel = useMemo(
    () => channels.find((c) => c.type === "WHATSAPP") || null,
    [channels]
  );
  const facebookChannel = useMemo(
    () => channels.find((c) => c.type === "FACEBOOK") || null,
    [channels]
  );

  const isWhatsAppConnected = useMemo(() => {
    if (!whatsappChannel) return false;
    const meta = (whatsappChannel.meta as any) || {};
    return meta.needsReconnect !== true;
  }, [whatsappChannel]);

  const isFacebookConnected = useMemo(() => {
    if (!facebookChannel) return false;
    const meta = (facebookChannel.meta as any) || {};
    return meta.needsReconnect !== true;
  }, [facebookChannel]);

  const isReadyForAutomation = isFacebookConnected && isWhatsAppConnected;

  async function loadTemplates() {
    if (!botId || !isWhatsAppConnected) {
      setTemplates([]);
      return;
    }

    try {
      setTemplatesLoading(true);
      setError(null);
      const res = await fetchWhatsappTemplates(botId, { limit: 50 });
      setTemplates(res.items);
    } catch (err: any) {
      console.error(err);
      setError(err?.message || "Failed to load WhatsApp templates.");
    } finally {
      setTemplatesLoading(false);
    }
  }

  async function loadAutomation() {
    if (!botId) return;
    try {
      setLeadConfigLoading(true);
      setError(null);
      const cfg = await fetchMetaLeadAutomation(botId);
      if (cfg) {
        setLeadConfigDraft({
          phoneFieldName: cfg.phoneFieldName || "phone_number",
          consentFieldName: cfg.consentFieldName || "whatsapp_opt_in",
          requiresWhatsappOptIn: cfg.requiresWhatsappOptIn,
          templateName: cfg.templateName || "",
          templateLanguage: cfg.templateLanguage || ""
        });
      } else {
        setLeadConfigDraft({
          phoneFieldName: "phone_number",
          consentFieldName: "whatsapp_opt_in",
          requiresWhatsappOptIn: true,
          templateName: "",
          templateLanguage: ""
        });
      }
    } catch (err: any) {
      console.error(err);
      setError(
        err?.message || "Failed to load Lead Ads → WhatsApp automation."
      );
    } finally {
      setLeadConfigLoading(false);
    }
  }

  // Load automation + templates when WhatsApp connectivity changes
  useEffect(() => {
    if (!botId) return;
    (async () => {
      await loadAutomation();
      await loadTemplates();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botId, isWhatsAppConnected]);

  const approvedTemplates = useMemo(
    () => templates.filter((tpl) => tpl.status === "APPROVED"),
    [templates]
  );

  // Initial enabled state – based on bot feature flags or having a template in automation
  useEffect(() => {
    if (!bot) return;

    const hasLeadFeature =
      !!bot.leadWhatsappMessages200 ||
      !!bot.leadWhatsappMessages500 ||
      !!bot.leadWhatsappMessages1000;

    const hasAutomationTemplate =
      !!leadConfigDraft.templateName && !!leadConfigDraft.templateLanguage;

    setEnabled(hasLeadFeature || hasAutomationTemplate);
  }, [bot, leadConfigDraft.templateName, leadConfigDraft.templateLanguage]);

  // If channels are not ready, force automation OFF
  useEffect(() => {
    if (!isReadyForAutomation) {
      setEnabled(false);
    }
  }, [isReadyForAutomation]);

  const selectedTemplate = useMemo(
    () =>
      approvedTemplates.find((tpl) => tpl.id === selectedTemplateId) || null,
    [approvedTemplates, selectedTemplateId]
  );

  // Template form helpers
  function handleFormChange<K extends keyof TemplateFormState>(
    key: K,
    value: TemplateFormState[K]
  ) {
    setFormState((prev) => ({
      ...prev,
      [key]: value
    }));
  }

  function handleNameChange(value: string) {
    const sanitized = value
      .toLowerCase()
      .replace(/[^a-z0-9_]/g, "_")
      .replace(/_{2,}/g, "_")
      .replace(/^_+|_+$/g, "");
    handleFormChange("name", sanitized);
  }

  function addButton() {
    setFormState((prev) => ({
      ...prev,
      buttons: [
        ...prev.buttons,
        {
          id: `btn-${Date.now()}-${prev.buttons.length}`,
          type: "QUICK_REPLY",
          text: ""
        }
      ]
    }));
  }

  function updateButton(id: string, patch: Partial<TemplateFormButton>) {
    setFormState((prev) => ({
      ...prev,
      buttons: prev.buttons.map((b) =>
        b.id === id ? { ...b, ...patch } : b
      )
    }));
  }

  function removeButton(id: string) {
    setFormState((prev) => ({
      ...prev,
      buttons: prev.buttons.filter((b) => b.id !== id)
    }));
  }

  function openCreate() {
    setFormMode("create");
    setFormTemplateId(null);
    setFormState(DEFAULT_FORM_STATE);
    setIsFormOpen(true);
  }

  function openEdit(tpl: WhatsappTemplate) {
    setFormMode("edit");
    setFormTemplateId(tpl.id);
    setFormState(formStateFromTemplate(tpl));
    setSelectedTemplateId(tpl.id);
    setIsFormOpen(true);
  }

  function closeForm() {
    setIsFormOpen(false);
  }

  async function handleTemplateSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!botId) return;
    setFormSubmitting(true);
    setError(null);

    const components = buildComponentsFromForm(formState);
    const payload: WhatsappTemplateUpsertPayload = {
      name: formState.name,
      category: formState.category,
      language: formState.language,
      components
    };

    try {
      if (formMode === "create") {
        await createWhatsappTemplate(botId, payload);
        setFormState(DEFAULT_FORM_STATE);
      } else if (formMode === "edit" && formTemplateId) {
        await updateWhatsappTemplate(botId, formTemplateId, payload);
      }

      await loadTemplates();
      setIsFormOpen(false);
    } catch (err: any) {
      console.error(err);
      setError(
        err?.message ||
          t("whatsappTemplates.messages.saveFailed", {
            defaultValue: "Failed to save template."
          })
      );
    } finally {
      setFormSubmitting(false);
    }
  }

  const handleBack = () => {
    navigate(-1);
  };

  const handleSelectTemplate = (tpl: WhatsappTemplate) => {
    setSelectedTemplateId(tpl.id);
    setLeadConfigDraft((prev) => ({
      ...prev,
      templateName: tpl.name,
      templateLanguage: tpl.language
    }));
  };

  const validateBeforeNext = (): string | null => {
    if (!enabled) {
      // Automation is off: no validation
      return null;
    }

    if (!isReadyForAutomation) {
      return t(
        "whatsappTemplates.onboarding.errors.channelsNotReady",
        "To enable this automation, connect both Facebook and WhatsApp in the Channels step."
      );
    }

    if (!leadConfigDraft.phoneFieldName?.trim()) {
      return t(
        "whatsappTemplates.leadAutomation.errors.missingPhoneField",
        "Please specify the phone field name from your lead forms."
      );
    }

    if (
      leadConfigDraft.requiresWhatsappOptIn &&
      !leadConfigDraft.consentFieldName?.trim()
    ) {
      return t(
        "whatsappTemplates.leadAutomation.errors.missingConsentField",
        "If you require explicit opt-in, you must specify the consent field name."
      );
    }

    if (!leadConfigDraft.templateName || !leadConfigDraft.templateLanguage) {
      return t(
        "whatsappTemplates.leadAutomation.errors.missingTemplate",
        "Please select an approved WhatsApp template."
      );
    }

    return null;
  };

  const persistFeatures = async (enable: boolean) => {
    if (!botId) return;
    await updateBot(botId, {
      // minimal tier on; you can adjust later in features/billing
      leadWhatsappMessages200: enable,
      leadWhatsappMessages500: false,
      leadWhatsappMessages1000: false
    });
  };

  const handleSkip = async () => {
    if (!botId) return;
    setSaving(true);
    setError(null);
    try {
      await persistFeatures(false);
      navigate(`/onboarding/bots/${encodeURIComponent(botId)}/plan`);
    } catch (err: any) {
      console.error(err);
      setError(
        err?.message ||
          t(
            "whatsappTemplates.onboarding.errors.save",
            "Could not save Lead Ads configuration."
          )
      );
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    if (!botId) return;

    const validationError = validateBeforeNext();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (enabled) {
        const payload: MetaLeadAutomationSettings = {
          phoneFieldName: leadConfigDraft.phoneFieldName || "phone_number",
          consentFieldName: leadConfigDraft.consentFieldName || "",
          requiresWhatsappOptIn: leadConfigDraft.requiresWhatsappOptIn,
          templateName: leadConfigDraft.templateName || "",
          templateLanguage: leadConfigDraft.templateLanguage || ""
        };

        await upsertMetaLeadAutomation(botId, payload);
        await persistFeatures(true);
      } else {
        await persistFeatures(false);
      }

      navigate(`/onboarding/bots/${encodeURIComponent(botId)}/plan`);
    } catch (err: any) {
      console.error(err);
      setError(
        err?.message ||
          t(
            "whatsappTemplates.onboarding.errors.save",
            "Could not save Lead Ads configuration."
          )
      );
    } finally {
      setSaving(false);
    }
  };

  if (!botId) {
    return null;
  }

  if (loading) {
    return (
      <OnboardingLayout
        currentStep="leadAds"
        botId={botId}
        title={t(
          "whatsappTemplates.onboarding.title",
          "Connect Facebook Lead Ads and WhatsApp automation"
        )}
        subtitle={t(
          "whatsappTemplates.onboarding.subtitle",
          "Choose or create a WhatsApp template and map the fields from your Lead Ads forms."
        )}
        layout="full"
      >
        <p className="muted">{t("common.loading", "Loading…")}</p>
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout
      currentStep="leadAds"
      botId={botId}
      title={t(
        "whatsappTemplates.onboarding.title",
        "Connect Facebook Lead Ads and WhatsApp automation"
      )}
      subtitle={t(
        "whatsappTemplates.onboarding.subtitle",
        "Choose or create a WhatsApp template and map the fields from your Lead Ads forms."
      )}
      layout="full"
    >
      <div className="leadads-layout">
        {/* Skip top-right */}
        <div className="onboarding-section-header">
          <div />
          <button
            type="button"
            className="btn-secondary btn-color"
            onClick={handleSkip}
            disabled={saving}
          >
            {t("whatsappTemplates.onboarding.common.skip", "Skip for now")}
          </button>
        </div>

        {/* Big orange warning bar */}
        {!isReadyForAutomation && (
          <div className="alert alert-warning leadads-warning">
            <div className="alert-title">
              {t(
                "whatsappTemplates.onboarding.warningTitle",
                "Connect channels to activate automation"
              )}
            </div>
            <div className="alert-body">
              {!isFacebookConnected &&
                t(
                  "whatsappTemplates.onboarding.warningFacebook",
                  "Connect a Facebook page in the Channels step so Coslo can read new leads."
                )}
              {!isFacebookConnected && !isWhatsAppConnected && " "}
              {!isWhatsAppConnected &&
                t(
                  "whatsappTemplates.onboarding.warningWhatsapp",
                  "Connect a WhatsApp Business number in the Channels step to send messages."
                )}
            </div>
          </div>
        )}

        {error && <div className="form-error onboarding-form-error-txt">{error}</div>}

        {/* Connection status + ON/OFF toggle */}
        <div className="leadads-status-row">
          <div className="leadads-status-main">
            <span className="leadads-status-label">
              {t(
                "whatsappTemplates.onboarding.requirementsTitle",
                "Connection status"
              )}
            </span>
            <div className="leadads-status-pills">
              <span className="leadads-status-pill">
                <span className="leadads-status-dot leadads-status-dot--fb" />
                Facebook{" "}
                {isFacebookConnected
                  ? t("common.connected", "connected")
                  : t("common.notConnected", "not connected")}
              </span>
              <span className="leadads-status-pill">
                <span className="leadads-status-dot leadads-status-dot--wa" />
                WhatsApp{" "}
                {isWhatsAppConnected
                  ? t("common.connected", "connected")
                  : t("common.notConnected", "not connected")}
              </span>
            </div>
          </div>

          <div className="leadads-toggle">
            <label className="booking-toggle">
              <input
                type="checkbox"
                checked={enabled}
                disabled={!isReadyForAutomation || saving}
                onChange={(e) => setEnabled(e.target.checked)}
              />
              <span className="booking-toggle-slider" />
              <span className="booking-toggle-label">
                {enabled
                  ? t(
                      "whatsappTemplates.onboarding.toggleOn",
                      "Lead Ads automation is ON"
                    )
                  : t(
                      "whatsappTemplates.onboarding.toggleOff",
                      "Lead Ads automation is OFF"
                    )}
              </span>
            </label>
            {!isReadyForAutomation && (
              <p className="muted small">
                {t(
                  "whatsappTemplates.onboarding.toggleDisabledHint",
                  "Connect both channels to turn this automation on."
                )}
              </p>
            )}
            {enabled && isReadyForAutomation && (
              <p className="muted small">
                {t(
                  "whatsappTemplates.onboarding.toggleOnHint",
                  "New leads from your Facebook forms will automatically receive a WhatsApp message using the template below."
                )}
              </p>
            )}
          </div>
        </div>

        {/* When automation is OFF, just show explanation + nav */}
        {!enabled && (
          <>
            <div className="leadads-disabled-hint">
              <p className="muted">
                {t(
                  "whatsappTemplates.onboarding.disabledHint",
                  "You can keep Lead Ads automation turned off for now and still complete onboarding. You’ll be able to come back here later from the app and turn it on."
                )}
              </p>
            </div>
            <div
              className="onboarding-form-actions"
              style={{ marginTop: "1.2rem" }}
            >
              <button
                className="btn-secondary btn-color"
                type="button"
                onClick={handleBack}
                disabled={saving}
              >
                {t("common.back", "Back")}
              </button>
              <button
                className="btn-primary"
                type="button"
                onClick={handleNext}
                disabled={saving}
              >
                {saving
                  ? t("common.saving", "Saving…")
                  : t(
                      "whatsappTemplates.onboarding.next",
                      "Review plan & pay"
                    )}
              </button>
            </div>
          </>
        )}

        {/* When automation is ON, show full config + templates grid */}
        {enabled && (
          <>
            <div className="leadads-grid">
              {/* LEFT: automation + mapping (no separate Save button) */}
              <section className="lead-card">
                <form
                  onSubmit={(e) => e.preventDefault()}
                  className="form"
                >
                  <header className="lead-card-header">
                    <div>
                      <h2>
                        {t(
                          "whatsappTemplates.leadAutomation.title",
                          "Lead Ads → WhatsApp automation"
                        )}
                      </h2>
                      <p className="lead-card-subtitle">
                        {t(
                          "whatsappTemplates.onboarding.automationHint",
                          "When a new lead arrives from Meta, Coslo will send a WhatsApp template and optionally check for explicit opt-in."
                        )}
                      </p>
                    </div>
                  </header>

                  {leadConfigLoading ? (
                    <p className="muted">
                      {t(
                        "whatsappTemplates.leadAutomation.loading",
                        "Loading automation settings…"
                      )}
                    </p>
                  ) : (
                    <>
                      <div className="form-grid-2">
                        <div className="form-field">
                          <label>
                            {t(
                              "whatsappTemplates.leadAutomation.phoneField",
                              "Phone field name"
                            )}
                          </label>
                          <input
                            type="text"
                            value={leadConfigDraft.phoneFieldName || ""}
                            onChange={(e) =>
                              setLeadConfigDraft((prev) => ({
                                ...prev,
                                phoneFieldName: e.target.value
                              }))
                            }
                            placeholder="phone_number"
                            required
                          />
                          <div className="muted small">
                            {t(
                              "whatsappTemplates.leadAutomation.phoneFieldHelp",
                              "Name of the form field that contains the phone number in the lead payload."
                            )}
                          </div>
                        </div>

                        <div className="form-field">
                          <label>
                            {t(
                              "whatsappTemplates.leadAutomation.consentField",
                              "Consent field name (optional)"
                            )}
                          </label>
                          <input
                            type="text"
                            value={leadConfigDraft.consentFieldName || ""}
                            onChange={(e) =>
                              setLeadConfigDraft((prev) => ({
                                ...prev,
                                consentFieldName: e.target.value
                              }))
                            }
                            placeholder="whatsapp_opt_in"
                          />
                          <div className="muted small">
                            {t(
                              "whatsappTemplates.leadAutomation.consentFieldHelp",
                              "Name of the yes/no field used for WhatsApp consent."
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="form-field form-field-inline">
                        <label className="booking-toggle">
                          <input
                            type="checkbox"
                            checked={leadConfigDraft.requiresWhatsappOptIn}
                            onChange={(e) =>
                              setLeadConfigDraft((prev) => ({
                                ...prev,
                                requiresWhatsappOptIn: e.target.checked
                              }))
                            }
                          />
                          <span className="booking-toggle-slider" />
                          <span className="booking-toggle-label">
                            {t(
                              "whatsappTemplates.leadAutomation.requireOptIn",
                              "Require explicit WhatsApp opt-in"
                            )}
                          </span>
                        </label>
                      </div>

                      <hr />

                      <div className="form-field">
                        <label>
                          {t(
                            "whatsappTemplates.leadAutomation.template",
                            "Template to send"
                          )}
                        </label>
                        <select
                          value={
                            leadConfigDraft.templateName &&
                            leadConfigDraft.templateLanguage
                              ? `${leadConfigDraft.templateName}:${leadConfigDraft.templateLanguage}`
                              : ""
                          }
                          onChange={(e) => {
                            const value = e.target.value;
                            setLeadConfigDraft((prev) => {
                              if (!value) {
                                return {
                                  ...prev,
                                  templateName: "",
                                  templateLanguage: ""
                                };
                              }
                              const [name, lang] = value.split(":");
                              return {
                                ...prev,
                                templateName: name,
                                templateLanguage: lang
                              };
                            });
                          }}
                          disabled={approvedTemplates.length === 0}
                          required
                        >
                          <option value="">
                            {t(
                              "whatsappTemplates.leadAutomation.templatePlaceholder",
                              "Select an approved template…"
                            )}
                          </option>
                          {approvedTemplates.map((tpl) => (
                            <option
                              key={tpl.id}
                              value={`${tpl.name}:${tpl.language}`}
                            >
                              {tpl.name} ({tpl.language})
                            </option>
                          ))}
                        </select>
                        <div className="muted small">
                          {t(
                            "whatsappTemplates.leadAutomation.templateHelp",
                            "Template used for new leads from Meta. Only approved templates are available."
                          )}
                        </div>
                      </div>

                      {selectedTemplate && (
                        <div className="leadads-template-preview">
                          <p className="muted small">
                            {t(
                              "whatsappTemplates.onboarding.previewLabel",
                              "Selected template:"
                            )}{" "}
                            <strong>
                              {selectedTemplate.name} (
                              {selectedTemplate.language})
                            </strong>
                          </p>
                        </div>
                      )}

                      <p className="muted small">
                        {t(
                          "whatsappTemplates.onboarding.autoSaveHint",
                          "Settings and templates will be saved when you click “Review plan & pay”."
                        )}
                      </p>
                    </>
                  )}
                </form>
              </section>

              {/* RIGHT: templates list & create/edit */}
              <section className="lead-card">
                <header className="lead-card-header lead-card-header--row">
                  <div>
                    <h2>
                      {t(
                        "whatsappTemplates.title",
                        "WhatsApp message templates"
                      )}
                    </h2>
                    {bot && (
                      <p className="lead-card-subtitle">
                        {t("whatsappTemplates.subtitle", {
                          defaultValue:
                            "Choose which template should be used for new leads from {{name}}.",
                          name: bot.name
                        })}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn-primary btn-sm"
                    onClick={openCreate}
                    disabled={!isWhatsAppConnected}
                  >
                    {t("whatsappTemplates.addTemplate", "New template")}
                  </button>
                </header>

                {templatesLoading ? (
                  <p className="muted">
                    {t(
                      "whatsappTemplates.loadingTemplates",
                      "Loading templates…"
                    )}
                  </p>
                ) : !isWhatsAppConnected ? (
                  <p className="muted">
                    {t(
                      "whatsappTemplates.templates.requireWhatsapp",
                      "Connect a WhatsApp number on the Channels step to sync and manage templates."
                    )}
                  </p>
                ) : templates.length === 0 ? (
                  <div className="wa-template-empty">
                    <p className="muted">
                      {t(
                        "whatsappTemplates.empty",
                        "No WhatsApp templates found for this bot yet."
                      )}
                    </p>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={openCreate}
                    >
                      {t(
                        "whatsappTemplates.emptyCta",
                        "Create your first template"
                      )}
                    </button>
                  </div>
                ) : (
                  <div className="lead-template-list">
                    {templates.map((tpl) => {
                      const isActive =
                        tpl.name === leadConfigDraft.templateName &&
                        tpl.language === leadConfigDraft.templateLanguage;

                      return (
                        <button
                          key={tpl.id}
                          type="button"
                          className={
                            "lead-template-row" +
                            (isActive ? " lead-template-row--active" : "")
                          }
                          onClick={() => handleSelectTemplate(tpl)}
                        >
                          <div className="lead-template-main">
                            <div className="lead-template-name">
                              {tpl.name}
                              <span className="lead-template-language">
                                {" "}
                                · {tpl.language}
                              </span>
                            </div>
                            <div className="lead-template-meta">
                              <span>{tpl.category}</span>
                            </div>
                          </div>
                          <div className="lead-template-status">
                            <span className={toStatusClass(tpl.status)}>
                              {tpl.status}
                            </span>
                            <button
                              type="button"
                              className="btn-secondary btn-xs btn-color"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEdit(tpl);
                              }}
                              disabled={!isWhatsAppConnected}
                            >
                              {t(
                                "whatsappTemplates.actions.edit",
                                "Edit"
                              )}
                            </button>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </section>
            </div>

            {/* Bottom wizard controls */}
            <div className="form-actions" style={{ marginTop: "1.2rem" }}>
              <button
                className="btn-secondary btn-color"
                type="button"
                onClick={handleBack}
                disabled={saving}
              >
                {t("common.back", "Back")}
              </button>
              <button
                className="btn-primary"
                type="button"
                onClick={handleNext}
                disabled={saving}
              >
                {saving
                  ? t("common.saving", "Saving…")
                  : t(
                      "whatsappTemplates.onboarding.next",
                      "Review plan & pay"
                    )}
              </button>
            </div>
          </>
        )}

        {/* Modal for create/edit template */}
        {isFormOpen && (
          <div className="wa-template-modal-backdrop">
            <div className="wa-template-modal">
              <div className="wa-template-modal-header">
                <h2>
                  {formMode === "create"
                    ? t(
                        "whatsappTemplates.modal.createTitle",
                        "New WhatsApp template"
                      )
                    : t(
                        "whatsappTemplates.modal.editTitle",
                        "Edit WhatsApp template"
                      )}
                </h2>
                <button
                  type="button"
                  className="btn-ghost btn-xs"
                  onClick={closeForm}
                >
                  ✕
                </button>
              </div>
              <form onSubmit={handleTemplateSubmit} className="form">
                <div className="form-grid-2">
                  <div className="form-field">
                    <label>
                      {t(
                        "whatsappTemplates.form.name",
                        "Template name"
                      )}
                    </label>
                    <input
                      type="text"
                      value={formState.name}
                      onChange={(e) => handleNameChange(e.target.value)}
                      disabled={formMode === "edit"}
                      placeholder={t(
                        "whatsappTemplates.form.namePlaceholder",
                        "e.g. order_update"
                      )}
                      required
                    />
                    <div className="muted small">
                      {t(
                        "whatsappTemplates.form.nameHelp",
                        "Lowercase, numbers and underscores only. This is not visible to end users."
                      )}
                    </div>
                  </div>

                  <div className="form-field">
                    <label>
                      {t(
                        "whatsappTemplates.form.language",
                        "Language"
                      )}
                    </label>
                    <input
                      type="text"
                      value={formState.language}
                      onChange={(e) =>
                        handleFormChange("language", e.target.value)
                      }
                      placeholder="en_US"
                      required
                    />
                    <div className="muted small">
                      {t(
                        "whatsappTemplates.form.languageHelp",
                        "Use WhatsApp language codes like en_US, it_IT, es_ES…"
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-grid-2">
                  <div className="form-field">
                    <label>
                      {t(
                        "whatsappTemplates.form.category",
                        "Category"
                      )}
                    </label>
                    <select
                      value={formState.category}
                      onChange={(e) =>
                        handleFormChange(
                          "category",
                          e.target.value as TemplateFormState["category"]
                        )
                      }
                    >
                      <option value="UTILITY">
                        {t(
                          "whatsappTemplates.category.utility",
                          "Utility"
                        )}
                      </option>
                      <option value="MARKETING">
                        {t(
                          "whatsappTemplates.category.marketing",
                          "Marketing"
                        )}
                      </option>
                      <option value="AUTHENTICATION">
                        {t(
                          "whatsappTemplates.category.authentication",
                          "Authentication"
                        )}
                      </option>
                    </select>
                  </div>
                </div>

                <hr />

                <div className="form-section">
                  <div className="form-field form-field-inline">
                    <label>
                      <input
                        type="checkbox"
                        checked={formState.headerEnabled}
                        onChange={(e) =>
                          handleFormChange("headerEnabled", e.target.checked)
                        }
                      />{" "}
                      {t(
                        "whatsappTemplates.form.header",
                        "Header (optional)"
                      )}
                    </label>
                  </div>
                  {formState.headerEnabled && (
                    <div className="form-field">
                      <textarea
                        value={formState.headerText}
                        onChange={(e) =>
                          handleFormChange("headerText", e.target.value)
                        }
                        maxLength={60}
                        placeholder={t(
                          "whatsappTemplates.form.headerPlaceholder",
                          "Short title shown above the body…"
                        )}
                      />
                      <div className="muted small">
                        {t(
                          "whatsappTemplates.form.headerHelp",
                          "Up to 60 characters. You can use variables like {{1}}."
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="form-section">
                  <div className="form-field">
                    <label>
                      {t("whatsappTemplates.form.body", "Body")}
                    </label>
                    <textarea
                      value={formState.bodyText}
                      onChange={(e) =>
                        handleFormChange("bodyText", e.target.value)
                      }
                      rows={5}
                      placeholder={t(
                        "whatsappTemplates.form.bodyPlaceholder",
                        "Main message text. Use {{1}}, {{2}}, … for variables."
                      )}
                      required
                    />
                    <div className="muted small">
                      {t(
                        "whatsappTemplates.form.bodyHelp",
                        "Keep it clear and concise. Variables will be filled at send time."
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <div className="form-field form-field-inline">
                    <label>
                      <input
                        type="checkbox"
                        checked={formState.footerEnabled}
                        onChange={(e) =>
                          handleFormChange("footerEnabled", e.target.checked)
                        }
                      />{" "}
                      {t(
                        "whatsappTemplates.form.footer",
                        "Footer (optional)"
                      )}
                    </label>
                  </div>
                  {formState.footerEnabled && (
                    <div className="form-field">
                      <textarea
                        value={formState.footerText}
                        onChange={(e) =>
                          handleFormChange("footerText", e.target.value)
                        }
                        maxLength={60}
                        placeholder={t(
                          "whatsappTemplates.form.footerPlaceholder",
                          "Small line below the message (e.g. legal or brand)."
                        )}
                      />
                    </div>
                  )}
                </div>

                <div className="form-section">
                  <div className="form-field">
                    <div className="form-label-row">
                      <label>
                        {t(
                          "whatsappTemplates.form.buttons",
                          "Buttons"
                        )}
                      </label>
                      <button
                        type="button"
                        className="btn-secondary btn-xs btn-color"
                        onClick={addButton}
                      >
                        {t(
                          "whatsappTemplates.form.addButton",
                          "Add button"
                        )}
                      </button>
                    </div>
                    <div className="muted small">
                      {t(
                        "whatsappTemplates.form.buttonsHelp",
                        "Optional quick reply, URL or phone buttons."
                      )}
                    </div>
                  </div>

                  {formState.buttons.map((btn) => (
                    <div
                      key={btn.id}
                      className="wa-template-button-row form-grid-3"
                    >
                      <div className="form-field">
                        <select
                          value={btn.type}
                          onChange={(e) =>
                            updateButton(btn.id, {
                              type: e.target
                                .value as TemplateFormButton["type"],
                              url:
                                e.target.value === "URL"
                                  ? btn.url
                                  : undefined,
                              phone_number:
                                e.target.value === "PHONE_NUMBER"
                                  ? btn.phone_number
                                  : undefined
                            })
                          }
                        >
                          <option value="QUICK_REPLY">
                            {t(
                              "whatsappTemplates.buttons.quickReply",
                              "Quick reply"
                            )}
                          </option>
                          <option value="URL">
                            {t(
                              "whatsappTemplates.buttons.url",
                              "URL"
                            )}
                          </option>
                          <option value="PHONE_NUMBER">
                            {t(
                              "whatsappTemplates.buttons.phone",
                              "Phone number"
                            )}
                          </option>
                        </select>
                      </div>
                      <div className="form-field">
                        <input
                          type="text"
                          value={btn.text}
                          onChange={(e) =>
                            updateButton(btn.id, {
                              text: e.target.value
                            })
                          }
                          placeholder={t(
                            "whatsappTemplates.buttons.textPlaceholder",
                            "Button label"
                          )}
                          required
                        />
                      </div>
                      <div className="form-field form-field-inline">
                        {btn.type === "URL" && (
                          <input
                            type="url"
                            value={btn.url || ""}
                            onChange={(e) =>
                              updateButton(btn.id, {
                                url: e.target.value
                              })
                            }
                            placeholder="https://example.com"
                            required
                          />
                        )}
                        {btn.type === "PHONE_NUMBER" && (
                          <input
                            type="tel"
                            value={btn.phone_number || ""}
                            onChange={(e) =>
                              updateButton(btn.id, {
                                phone_number: e.target.value
                              })
                            }
                            placeholder="+1 555 123456"
                            required
                          />
                        )}
                        <button
                          type="button"
                          className="btn-ghost btn-xs"
                          onClick={() => removeButton(btn.id)}
                        >
                          {t(
                            "whatsappTemplates.buttons.remove",
                            "Remove"
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={formSubmitting}
                  >
                    {formSubmitting
                      ? t("common.saving", "Saving…")
                      : formMode === "create"
                      ? t(
                          "whatsappTemplates.actions.create",
                          "Create template"
                        )
                      : t(
                          "whatsappTemplates.actions.update",
                          "Update template"
                        )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </OnboardingLayout>
  );
};

export default OnboardingLeadAdsPage;
